import { useStyles } from "@/styles/useStyles";
import { useEffect, useMemo, useState } from "react";
import { Group, Stack, Avatar, Space, Checkbox, Button } from "@mantine/core";
import { Text } from "@/components/ui/Text";
import {
  IconArrowRight,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { Advizer } from "@/models/Advizer";
import { AssignmentItemTimeProgress } from "./AssignmentItemTimeProgress";
import { config } from "@/config";
import advizerLogo from "@/assets/advize-logo-light.jpg";

export const AdvizerCheckbox = ({
  advizerId,
  isSmall = false,
  isAssignmentResultItem = false,
  advizerData,
  isCompleted,
  isExportView = false,
  hideCheckbox = false,
}: {
  advizerId?: string | null;
  index: number;
  isSmall?: boolean;
  isAssignmentResultItem?: boolean;
  advizerData?: any;
  isCompleted?: boolean;
  isExportView?: boolean;
  hideCheckbox?: boolean;
}) => {
  const { theme } = useStyles();
  const [showDetails, setShowDetails] = useState(isExportView);

  const advizer = useMemo(() => {
    if (!advizerData) {
      return null;
    }
    return new Advizer(advizerData.advizer);
  }, [advizerData]);

  useEffect(() => {
    if (isExportView) {
      setShowDetails(true);
    } else {
      setShowDetails(false);
    }
  }, [isExportView]);

  const AdvizerTitle = useMemo(() => {
    return (
      <Group px={0} wrap="nowrap">
        {
          advizer ? (
            <Avatar
              src={advizer?.profilePictureURL || advizerLogo}
              size={isSmall ? "sm" : "md"}

              // color={theme.colors.primary[8]}
            />
          ) : null
          // <Box
          //   style={{
          //     display: "flex",
          //     alignItems: "center",
          //     justifyContent: "center",
          //     width: 40,
          //     height: 40,
          //     borderRadius: 40,
          //     backgroundColor: theme.colors.primary[0],
          //   }}
          // >
          //   <IconUserPlus size={20} />
          // </Box>
        }
        {advizer ? (
          <Stack gap={4}>
            <Group wrap="nowrap" flex={1} pr="md">
              <a
                href={`/advizers/${advizer?.id}`}
                target="_blank"
                style={{
                  flex: 1,
                  alignItems: "center",
                  width: "100%",
                  display: "flex",
                }}
              >
                <Text
                  titleStyle={isSmall ? "h6" : "h5"}
                  lh={1.2}
                  fw={500}
                  lineClamp={isExportView ? undefined : 1}
                  c={theme.colors.text[9]}
                  style={{ flex: 1 }}
                  // pr={1}
                >
                  {advizer?.firstName},{" "}
                  <span style={{ color: theme.colors.text[7] }}>
                    {advizer?.jobTitle} @ {advizer?.publicCompanyName}
                  </span>
                </Text>

                {isExportView ? null : (
                  <IconArrowRight
                    size={20}
                    style={{ marginLeft: 2 }}
                    color={theme.colors.text[7]}
                  />
                )}
              </a>
            </Group>

            {isExportView ? (
              <Text size="xs">
                <a href={`${window.location.origin}/advizers/${advizer?.id}`}>
                  {`${window.location.origin}/advizers/${advizer?.id}`}
                </a>
              </Text>
            ) : null}
          </Stack>
        ) : isAssignmentResultItem ? (
          <Text titleStyle="h6" fw={500} c={theme.colors.text[9]} mt={6} pl={0}>
            No advizer for this slot yet
          </Text>
        ) : (
          <a
            href={`/explore`}
            target="_blank"
            style={{
              flex: 1,
              alignItems: "center",
              width: "100%",
              display: "flex",
              color: theme.colors.primary[8],
              marginTop: isSmall ? 4 : 4,
            }}
          >
            <Text
              titleStyle={isSmall ? "h6" : "h5"}
              fw={isSmall || !advizerId ? 500 : 700}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              Find an Advizer to fill this spot
              <IconArrowRight size={20} style={{ marginLeft: 4 }} />
            </Text>
          </a>
        )}
      </Group>
    );
  }, [
    advizer,
    advizerId,
    isAssignmentResultItem,
    isExportView,
    isSmall,
    theme.colors.primary,
    theme.colors.text,
  ]);

  return (
    <Stack gap={4} w="100%" flex={1}>
      <Group
        flex={1}
        gap={0}
        // opacity={isCompleted ? 0.7 : 1}
        align="center"
        w="100%"
      >
        {hideCheckbox ? null : (
          <>
            <Checkbox
              color="primary"
              size={isSmall ? "xs" : "md"}
              readOnly
              checked={isCompleted}
            />
            <Space w="md" />
          </>
        )}
        <Stack flex={1} gap={0}>
          <Group
            gap={0}
            pl={isSmall ? 4 : 8}
            flex={1}
            justify="space-between"
            w="100%"
          >
            {AdvizerTitle}
          </Group>
          <Space h={4} />
        </Stack>
      </Group>
      {advizerData ? (
        <Group
          flex={1}
          justify="space-between"
          wrap="nowrap"
          pl="xl"
          pt={isExportView ? 8 : undefined}
          w="100%"
        >
          <AssignmentItemTimeProgress
            totalSecondsWatched={advizerData?.totalSecondsWatched || 0}
            totalSecondsRequired={advizerData?.totalVideoDurationSeconds || 0}
            // isSmall={isSmall}
          />
          {isExportView ? null : (
            <Button
              size="xs"
              color="primary"
              variant="subtle"
              miw="min-content"
              onClick={() => setShowDetails(!showDetails)}
              leftSection={
                showDetails ? (
                  <IconChevronUp size={16} />
                ) : (
                  <IconChevronDown size={16} />
                )
              }
            >
              {showDetails ? "Hide Details" : "Show Details"}
            </Button>
          )}
        </Group>
      ) : null}
      {advizerData ? (
        <>
          {showDetails ? (
            <>
              <Space h="xs" />
              <Stack opacity={0.75} pl="md">
                {!advizerData?.videos
                  ? null
                  : advizerData.videos
                      .filter((video: any) => video.duration > 0)
                      .sort((a: any, b: any) => {
                        return a.question.number - b.question.number;
                      })
                      .map((videoData: any) => {
                        const isCompleted =
                          videoData?.userSecondsWatched / videoData?.duration >=
                          config.minPercentWatchedToBeComplete;

                        return (
                          <Group key={videoData.id} align="center" pl="md" gap="xs">
                            <Checkbox
                              color="primary"
                              size="xs"
                              readOnly
                              checked={isCompleted}
                            />
                            <Text titleStyle="h6" fw={500}>
                              Q{videoData?.question?.number}:{" "}
                              {videoData?.question?.title}
                            </Text>

                            {advizerId && (
                              <Group pl="xs" flex={1} miw={200}>
                                <AssignmentItemTimeProgress
                                  totalSecondsWatched={
                                    videoData?.userSecondsWatched || 0
                                  }
                                  totalSecondsRequired={
                                    videoData?.duration || 0
                                  }
                                  isSmall
                                />
                              </Group>
                            )}
                          </Group>
                        );
                      })}
              </Stack>
              <Space h="md" />
            </>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
};
